import * as amplitude from "@amplitude/unified";
import { trackEvent } from "./amplitude";

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

/** Shared event properties sent with navigation and search events. */
function getCommonEventProperties(): Record<string, string | number> {
  const props: Record<string, string | number> = {
    page_url: isBrowser() ? window.location.href : "",
    timestamp: new Date().toISOString(),
  };

  const userId = amplitude.getUserId();
  if (userId) {
    props.user_id = userId;
  }

  const sessionId = amplitude.getSessionId();
  if (sessionId !== undefined) {
    props.session_id = String(sessionId);
  }

  return props;
}

export interface SidebarSectionClickedProperties {
  section_name: string;
  section_position: number;
  section_category: string;
}

export function trackSidebarSectionClicked(
  properties: SidebarSectionClickedProperties,
): void {
  trackEvent("Sidebar Section Clicked", {
    ...getCommonEventProperties(),
    ...properties,
  });
}

export interface ForBusinessLinkClickedProperties {
  link_location: string;
  destination: string;
}

export function trackForBusinessLinkClicked(
  properties: ForBusinessLinkClickedProperties,
): void {
  trackEvent("For Business Link Clicked", {
    ...getCommonEventProperties(),
    ...properties,
  });
}

export interface ForBusinessCtaClickedProperties {
  cta_id: string;
  cta_location: string;
  cta_label: string;
  destination: string;
}

export function trackForBusinessCtaClicked(
  properties: ForBusinessCtaClickedProperties,
): void {
  trackEvent("For Business CTA Clicked", {
    ...getCommonEventProperties(),
    ...properties,
  });
}

export interface SearchInitiatedProperties {
  search_query: string;
  query_length: number;
  search_location: string;
}

export function trackSearchInitiated(
  properties: SearchInitiatedProperties,
): void {
  trackEvent("Search Initiated", {
    ...getCommonEventProperties(),
    ...properties,
  });
}

export interface SearchResultClickedProperties {
  search_query: string;
  result_clicked: string;
  result_position: number;
  time_to_click_ms: number;
  search_location?: string;
}

export function trackSearchResultClicked(
  properties: SearchResultClickedProperties,
): void {
  trackEvent("Search Result Clicked", {
    search_location: "navbar",
    ...getCommonEventProperties(),
    ...properties,
  });
}

export interface SearchAbandonedProperties {
  search_query: string;
  query_length: number;
  time_spent_ms: number;
  search_location?: string;
}

export function trackSearchAbandoned(
  properties: SearchAbandonedProperties,
): void {
  trackEvent("Search Abandoned", {
    search_location: "navbar",
    ...getCommonEventProperties(),
    ...properties,
  });
}

export function setAmplitudeUserRole(roleName: string | undefined): void {
  if (!isBrowser()) return;

  const identify = new amplitude.Identify();
  if (roleName) {
    identify.set("user_role", roleName);
  } else {
    identify.unset("user_role");
  }
  amplitude.identify(identify);
}
